// Shared onboarding routing rules.
//
// Full flow (user.onboard === 0):
//   intro -> path-choice -> simulation-selection -> team-evaluation-survey -> welcome
//
// Partial flow (user.onboard === 1 AND the course is missing a game
// or a survey decision): same steps, but skip 'path-choice' (the
// "review a demo" page) since the instructor has already seen it.
//
// In BOTH flows we also skip:
//   - simulation-selection if a game already exists for the current course
//   - team-evaluation-survey if the course has `survey_included` set to
//     "yes" or "no" (instructor already decided; null means undecided).
// This prevents re-prompting an instructor who already said "No" to
// peer evaluation, which was the leak in the old survey-row count.

import Auth from '../../../routes/Auth';
import { fetchAppGeneralInfo } from '../../../api/AppGeneralInfoService';

export const ONBOARDING_STEPS = [
  'intro',
  'path-choice',
  'simulation-selection',
  'team-evaluation-survey',
];

// Compute the URL for the next step given the current step and the
// per-course setup flags. Returns '/instructor/welcome' when nothing
// remains. currentStep=null means "starting fresh, pick the first step."
export const getNextOnboardingStep = (currentStep, { hasGame, hasSurvey, onboardComplete }) => {
  const skipPathChoice = onboardComplete;
  const skipSimSelection = hasGame;
  const skipSurvey = hasSurvey;

  const isSkipped = (step) => (
    (step === 'path-choice' && skipPathChoice)
    || (step === 'simulation-selection' && skipSimSelection)
    || (step === 'team-evaluation-survey' && skipSurvey)
  );

  const currentIdx = currentStep ? ONBOARDING_STEPS.indexOf(currentStep) : -1;
  const next = ONBOARDING_STEPS.find((step, idx) => idx > currentIdx && !isSkipped(step));

  const resultUrl = !next ? '/instructor/welcome' : `/instructor/onboarding/${next}`;
  console.log('[MG:onboarding] getNextOnboardingStep:', {
    currentStep, hasGame, hasSurvey, onboardComplete,
    skipPathChoice, skipSimSelection, skipSurvey,
    next, resultUrl,
  });
  return resultUrl;
};

// Kept as a no-op export so TeamEvaluationSurvey can still call it
// without breaking. The old session cache went away when survey_row
// counting was replaced with course.survey_included.
export const clearOnboardingSetupCache = () => {};

// Fetch { hasGame, hasSurvey } for the current course. Both are
// derived from general-info's cached payload:
//   - hasGame   : any game in data.games tied to the current course
//   - hasSurvey : the course's survey_included is "yes" or "no"
//                 (null means instructor hasn't decided yet, so the
//                 team-evaluation-survey step should still fire).
//
// Rejects when the network call fails. Do not swallow and fabricate
// { hasGame: false, hasSurvey: false } here — resolveOnboardingEntry
// treats that shape as "course has nothing set up" and would wrongly
// redirect an already-onboarded instructor to the onboarding flow on
// a transient failure (e.g. brief WiFi drop).
export const fetchOnboardingSetup = () => {
  const courseId = Number(Auth.getCourseId());
  const gameId = Number(Auth.getGameId());

  return fetchAppGeneralInfo(gameId, 'instructor')
    .then(data => {
      const games = (data && data.games) || [];
      const courses = (data && data.courses) || [];
      const hasGame = games.some(g => Number(g.course) === courseId);
      const course = courses.find(c => Number(c.id) === courseId);
      const decision = course ? course.survey_included : null;
      const hasSurvey = decision === 'yes' || decision === 'no';
      return { hasGame, hasSurvey };
    });
};

// Entry-point decision used by /start (start.js). Returns the URL
// the instructor should be sent to, or null when they don't need
// onboarding at all (fall through to existing welcome routing).
export const resolveOnboardingEntry = () => {
  const user = Auth.getUser();
  if (!user) return Promise.resolve(null);
  const onboardComplete = user.onboard !== 0;

  return fetchOnboardingSetup()
    .then(({ hasGame, hasSurvey }) => {
      if (!onboardComplete) {
        // Bypass the full flow (intro + path-choice) when the course
        // is already fully set up. Return null so start.js's
        // continueInstructorRouting handles the landing, and so
        // App.js's per-navigation gate doesn't yank the user back to
        // /instructor/welcome on every left-nav click (that's what
        // returning a URL here would do, since the App.js caller fires
        // on every non-onboarding location change).
        if (hasGame && hasSurvey) {
          return null;
        }
        return getNextOnboardingStep(null, { hasGame, hasSurvey, onboardComplete: false });
      }
      if (!hasGame || !hasSurvey) {
        return getNextOnboardingStep(null, { hasGame, hasSurvey, onboardComplete: true });
      }
      return null;
    })
    // Network / fetch failure: don't redirect. Returning null keeps
    // the instructor on their current page instead of bouncing them
    // into onboarding based on fabricated "nothing is set up" data.
    // The next successful fetch (on the next navigation) will route
    // correctly.
    .catch(() => null);
};



// WEBPACK FOOTER //
// ./src/instructor/components/onboarding/onboardingRouting.js